import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { Stack, Typography } from "@mui/material";
import { useAdminStats } from "@/hooks/use-admin-stats";
import { AdminSummary } from "@/pages/users/data";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdminUsers } from "@/hooks/use-admin-users";
import CustomSearchInput from "@/components/custom/custom-search-input";
import CustomSelectInput from "@/components/custom/custom-select-input";
import { DataTable } from "@/components/custom/data-table";
import { AdminColumns } from "./_components/admin-column";
import CustomPagination from "@/components/custom/custom-pagination";
import { useAdminRoles } from "@/hooks/use-admin-role";
import CustomSimpleCard from "@/components/custom/custom-simple-card";
import { CustomButton } from "@/components/custom/custom-button";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { getAdminUsers } from "@/services/role-access.service";
import { useExportCsv } from "@/hooks/use-export-csv";
import NewAdminDialog from "./_components/new-admin-dialog";
import { createAdminUser } from "@/services/users.service";
import { formatRole } from "@/utils/format-role";

const SectionRoot = styled("section")(({ theme }) => ({
  margin: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const Card = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: "16px 20px",
  borderRadius: "8px",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const AdminUsers = () => {
  const [newAdminOpen, setNewAdminOpen] = useState(false);

  const [searchParams] = useSearchParams();
  const searchValue = searchParams.get("search") || "";
  const navigate = useNavigate();

  const currentPage = Number(searchParams.get("page") || 0);
  const pageSize = Number(searchParams.get("size") || 10);
  const roleName = searchParams.get("roleName");

  const queryParams = {
    page: currentPage,
    size: pageSize,
    roleName: roleName ? roleName : undefined,
    search: searchValue,
  };

  const {
    data: users,
    isLoading: isUsersLoading,
    isError,
  } = useAdminUsers(true, queryParams);
  const { stats, isLoading: isStatsLoading } = useAdminStats();

  const { data: roles } = useAdminRoles();

  const roleOptions =
    roles?.data?.map((item) => ({
      label: formatRole(item.name),
      value: item.name,
    })) ?? [];

  const { exportToCsv, isExporting } = useExportCsv({
    fetchFn: getAdminUsers,
    params: queryParams,
    filename: "admin-users",
    transform: (res) =>
      res?.data?.content?.map(({ firstName, surname, email, customerType, id, createdAt }) => ({
        "Full Name": `${firstName ?? ""} ${surname ?? ""}`.trim(),
        "Email Address": email,
        Role: customerType,
        "User ID": id,
        "Created At": new Date(createdAt).toLocaleDateString(),
      })) ?? [],
  });

  return (
    <SectionRoot>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
        {AdminSummary.map(({ title, objKeyRepr, icon }, idx) => (
          <CustomSimpleCard
            key={`${objKeyRepr}-${idx}`}
            title={title}
            icon={icon}
            isLoading={isStatsLoading}
          >
            <Typography variant="h6">{stats?.[objKeyRepr] ?? 0}</Typography>
          </CustomSimpleCard>
        ))}
      </Stack>

      <Card>
        <Stack
          direction={"row"}
          spacing={3}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction={"row"} spacing={3} alignItems="center">
            <CustomSearchInput
              paramKey="search"
              placeholder="Search admins..."
            />
            <CustomSelectInput
              paramKey="roleName"
              label="Roles"
              placeholder="All Roles"
              options={roleOptions}
            />
          </Stack>
          <Stack direction={"row"} spacing={1} alignItems="center">
            <CustomButton
              variantType="outline"
              onClick={() => navigate("/users/admins/manage-roles")}
              sx={{ height: 35 }}
            >
              Mangage Roles
            </CustomButton>
            <CustomButton
              variantType="outline"
              endIcon={<FileDownloadOutlinedIcon fontSize="small" />}
              onClick={exportToCsv}
              isLoading={isExporting}
              sx={{ height: 35 }}
            >
              {isExporting ? "Exporting..." : "Export"}
            </CustomButton>
            <CustomButton
              onClick={() => setNewAdminOpen(true)}
              sx={{ height: 35 }}
            >
              New Admin
            </CustomButton>
          </Stack>
        </Stack>

        <DataTable
          columns={AdminColumns}
          data={users?.data?.content ?? []}
          isLoading={isUsersLoading}
          isError={isError}
        />
        <CustomPagination
          total={users?.data?.totalItems ?? 0}
          perPageOptions={[5, 10, 20, 50, 100]}
        />
      </Card>

      <NewAdminDialog
        open={newAdminOpen}
        onClose={() => setNewAdminOpen(false)}
        mutationFn={createAdminUser}
        roleOptions={roleOptions}
      />
    </SectionRoot>
  );
};

export default AdminUsers;
